import { Form, InputNumber, Radio } from "antd";
import { ReactNode, useEffect } from "react";
import { TbInfoCircle } from "react-icons/tb";
import {
  InitialPaymentPercentageOf,
  InterestCalculationStrategy,
  PaymentType,
  TargetWeeklyCalculationStrategy,
} from "dtos/strategy.dto";
import { MONTHLY_WEEK_CALCULATIONS, WIZARD_INTEREST_STRATEGIES } from "../functions";
import { AnswerButtons, ChoiceCards, ChoiceOption, PercentInput, PushbackInput, Question, StepHeading } from "./ui";

const INTEREST_OPTIONS: Record<InterestCalculationStrategy, ChoiceOption<InterestCalculationStrategy>> = {
  [InterestCalculationStrategy.PERCENTAGE]: {
    value: InterestCalculationStrategy.PERCENTAGE,
    title: "Percentage of debt",
    description: "Program + success fee as a % of the debt.",
  },
  [InterestCalculationStrategy.FIXED_PER_LAWSUIT]: {
    value: InterestCalculationStrategy.FIXED_PER_LAWSUIT,
    title: "Fixed per lawsuit",
    description: "A flat amount for each active lawsuit.",
  },
  [InterestCalculationStrategy.FIXED]: {
    value: InterestCalculationStrategy.FIXED,
    title: "Fixed amount",
    description: "Legacy option kept from the saved strategy.",
  },
  [InterestCalculationStrategy.FIXED_PER_LOAN]: {
    value: InterestCalculationStrategy.FIXED_PER_LOAN,
    title: "Fixed per loan",
    description: "Legacy option kept from the saved strategy.",
  },
};

/** Read-only line explaining a value the purpose decided for the user. */
const InfoNote = ({ children }: { children: ReactNode }) => (
  <div className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-600">
    <TbInfoCircle className="shrink-0" />
    {children}
  </div>
);

const PaymentsStep = () => {
  const form = Form.useFormInstance();
  const paymentType: PaymentType | undefined = Form.useWatch("payment_type", form);
  const weekCalculation: TargetWeeklyCalculationStrategy | undefined = Form.useWatch(
    "target_weekly_calculation_strategy",
    form,
  );
  const interestStrategy: InterestCalculationStrategy | undefined = Form.useWatch(
    "interest_calculation_strategy",
    form,
  );
  const isBiasExclusive: boolean | undefined = Form.useWatch("is_bias_exclusive", { form, preserve: true });
  const initialFeeBase: InitialPaymentPercentageOf | undefined = Form.useWatch("initial_fee_base", {
    form,
    preserve: true,
  });
  const isMonthly = paymentType === PaymentType.MONTHLY;

  // Only prior bias / compact apply to monthly plans in this flow.
  useEffect(() => {
    if (isMonthly && weekCalculation && !MONTHLY_WEEK_CALCULATIONS.includes(weekCalculation)) {
      form.setFieldValue("target_weekly_calculation_strategy", TargetWeeklyCalculationStrategy.PRIOR_BIAS);
    }
  }, [form, isMonthly, weekCalculation]);

  const interestOptions = [
    ...WIZARD_INTEREST_STRATEGIES,
    ...(interestStrategy && !WIZARD_INTEREST_STRATEGIES.includes(interestStrategy) ? [interestStrategy] : []),
  ].map((value) => INTEREST_OPTIONS[value]);

  return (
    <>
      <StepHeading step={2} title="Payments & fees" description="How often the customer pays, and what we charge." />

      <Question title="How often will the customer pay?">
        <Form.Item name="payment_type" noStyle>
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            size="large"
            options={[
              { label: "One-time", value: PaymentType.ONE_TIME },
              { label: "Weekly", value: PaymentType.WEEKLY },
              { label: "Monthly", value: PaymentType.MONTHLY },
            ]}
          />
        </Form.Item>
        {isMonthly && (
          <div className="mt-3 px-4 py-3.5 bg-violet-50 border border-solid border-violet-200 rounded-lg">
            <div className="text-sm font-semibold text-violet-950 mb-2.5">
              Should the weekly amount be multiplied by 4 for each month?
            </div>
            <Form.Item name="target_weekly_calculation_strategy" noStyle>
              <AnswerButtons<TargetWeeklyCalculationStrategy>
                options={[
                  { value: TargetWeeklyCalculationStrategy.PRIOR_BIAS, label: "Yes, 4 × weekly" },
                  { value: TargetWeeklyCalculationStrategy.COMPACT, label: "No, keep it compact" },
                ]}
              />
            </Form.Item>
            <div className="text-xs text-violet-800 mt-2">
              {weekCalculation === TargetWeeklyCalculationStrategy.COMPACT
                ? "Each month collects the weekly amount once, stretching the plan."
                : "Each month collects 4 × the weekly amount."}
            </div>
          </div>
        )}
      </Question>

      <div className="grid grid-cols-2 gap-4">
        <Question title="Initial fee" hint="Commission collected with the first payment.">
          <Form.Item name="initial_fee" noStyle>
            <PercentInput aria-label="Initial fee" />
          </Form.Item>
          <InfoNote>
            {initialFeeBase === InitialPaymentPercentageOf.TOTAL_INTEREST
              ? "Percentage of the total interest"
              : "Percentage of the total debt"}
          </InfoNote>
        </Question>
        <Question title="Upfront amount" hint="A fixed amount collected with the first payment.">
          <Form.Item name="initial_bias_amount" noStyle>
            <InputNumber aria-label="Upfront amount" prefix="$" min={0} className="w-full" />
          </Form.Item>
          <InfoNote>
            {isBiasExclusive ? "Exclusive — added on top of the commission" : "Inclusive — taken out of the commission"}
          </InfoNote>
        </Question>
      </div>

      <Question title="How is the commission calculated?">
        <Form.Item name="interest_calculation_strategy" noStyle>
          <ChoiceCards<InterestCalculationStrategy> columns={2} options={interestOptions} />
        </Form.Item>
        {interestStrategy === InterestCalculationStrategy.PERCENTAGE ? (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Form.Item name="program_fee" label="Program fee" className="!mb-0">
              <PercentInput />
            </Form.Item>
            <Form.Item name="success_fee" label="Success fee" className="!mb-0">
              <PercentInput />
            </Form.Item>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Form.Item
              name="interest_amount"
              label={
                interestStrategy === InterestCalculationStrategy.FIXED_PER_LAWSUIT ? "Amount per lawsuit" : "Amount"
              }
              className="!mb-0"
            >
              <InputNumber prefix="$" min={0} className="w-full" />
            </Form.Item>
          </div>
        )}
      </Question>

      <Question title="Adjust the weekly payment?" hint="Push the weekly amount up or down from the customer’s budget.">
        <Form.Item name="dynamic_weekly_pushback" noStyle>
          <PushbackInput />
        </Form.Item>
      </Question>
    </>
  );
};

export default PaymentsStep;
